/**
 * The crack layout of one ball's wax shell as a texture-space field (pure maths, no DOM, no three):
 * a spherical Voronoi of the plate seeds with jagged borders, evaluated per texel of an equirect map.
 *
 * Per texel it stores the nearest plate (owner), the second-nearest plate (the neighbour across the
 * closest border) and the distance to that border. The shader opens a crack wherever either plate is
 * cracked and the distance is below the crack's half width, so a crack is symmetric about the border
 * and its edge comes from a smoothly interpolated distance: thin, anti-aliased, no stair-steps.
 *
 * Layout (matches three's SphereGeometry uv and DataTexture with flipY = false):
 *   column x ↔ u = (x + 0.5) / W (phi = 2πu, wraps); row y ↔ v = (y + 0.5) / H, row 0 = south pole.
 *   direction = (−cos φ · sin θ, cos θ, sin φ · sin θ) with θ = (1 − v) π.
 * Distances are in "field texels" = angle / (π / FIELD_H) — the 1024×512 field's texel, whatever size a field is
 * computed at — so a width means the same on the whole sphere and on every ball (the big ball's 2048×1024 field
 * just draws the same cracks twice as sharp).
 */

export const FIELD_W = 1024;
export const FIELD_H = 512;
/** dist byte = texels × DIST_SCALE (max 255 / 16 ≈ 16 texels ≈ 0.1 rad) */
export const DIST_SCALE = 16;

export type FieldResult = {
  /** RG pairs per texel: owner plate id, second plate id */
  ids: Uint8Array;
  /** distance to the nearest border, texels × DIST_SCALE */
  dist: Uint8Array;
  /** solid angle of each plate (sr), from the texel areas */
  area: Float32Array;
  /**
   * The finer crack layer, RG pairs per texel: distance to the nearest border of a much smaller cell pattern
   * (texels × DIST_SCALE) and a random byte per cell. A plate crushed under the finger shatters along these
   * lines into small fragments that stay on the clay, each tipped and pressed in by its own amount.
   */
  fine?: Uint8Array;
  /** size of the maps */
  w: number;
  h: number;
  ms: number;
};

// cheap 3D value noise for the domain warp (same family as the first painted version)
function hash3(x: number, y: number, z: number) {
  let h = (x * 374761393 + y * 668265263 + z * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * The warp's value noise with its 8 lattice hashes cached in `c` ([xi, yi, zi, a000 … a111]): along a
 * row the lattice cell rarely changes, so most calls skip the hashing. Same arithmetic, same result.
 */
function vnoise(x: number, y: number, z: number, c: Float64Array) {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const fx = x - xi, fy = y - yi, fz = z - zi;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy), sz = fz * fz * (3 - 2 * fz);
  if (xi !== c[0] || yi !== c[1] || zi !== c[2]) {
    c[0] = xi; c[1] = yi; c[2] = zi;
    c[3] = hash3(xi, yi, zi); c[4] = hash3(xi + 1, yi, zi); c[5] = hash3(xi, yi + 1, zi); c[6] = hash3(xi + 1, yi + 1, zi);
    c[7] = hash3(xi, yi, zi + 1); c[8] = hash3(xi + 1, yi, zi + 1); c[9] = hash3(xi, yi + 1, zi + 1); c[10] = hash3(xi + 1, yi + 1, zi + 1);
  }
  const x00 = c[3] + (c[4] - c[3]) * sx, x10 = c[5] + (c[6] - c[5]) * sx, x01 = c[7] + (c[8] - c[7]) * sx, x11 = c[9] + (c[10] - c[9]) * sx;
  const y0 = x00 + (x10 - x00) * sy, y1 = x01 + (x11 - x01) * sy;
  return (y0 + (y1 - y0) * sz) * 2 - 1;
}

/** Field index (y * W + x) of a unit direction, same convention as the texture. */
export function dirToField(x: number, y: number, z: number, W = FIELD_W, H = FIELD_H): number {
  let phi = Math.atan2(z, -x);
  if (phi < 0) phi += Math.PI * 2;
  const theta = Math.acos(Math.max(-1, Math.min(1, y)));
  const fx = Math.min(W - 1, Math.floor((phi / (Math.PI * 2)) * W));
  const fy = Math.min(H - 1, Math.max(0, Math.floor((1 - theta / Math.PI) * H)));
  return fy * W + fx;
}

/** Unit direction of field texel (x, y). */
export function fieldDir(x: number, y: number, out: Float32Array, W = FIELD_W, H = FIELD_H) {
  const phi = ((x + 0.5) / W) * Math.PI * 2, theta = (1 - (y + 0.5) / H) * Math.PI, st = Math.sin(theta);
  out[0] = -Math.cos(phi) * st; out[1] = Math.cos(theta); out[2] = Math.sin(phi) * st;
  return out;
}

/**
 * Candidate lists for the nearest-two-seeds search, on a cube map of M×M cells per face, indexed by the
 * WARPED direction itself (so the warp amplitude never enters the bound).
 *
 * A cell lies inside a cap (centre c, radius r = farthest corner; a gnomonic cell is the radial image of a
 * planar convex quad and cos-angle-to-c is quasi-concave there, so the corners are the farthest points).
 * Let a₂ be the angle from c to its second-nearest seed. For any point p in the cell both of c's nearest
 * seeds are within a₂ + r, so p's second-nearest seed is within a₂ + r; a seed with angle(c, s) > a₂ + 2r
 * is farther than a₂ + r from every p (triangle inequality), strictly behind p's top two, so it can be
 * neither of them and can't change a tie. Each cell keeps the seeds with angle ≤ a₂ + 2r (+1e-6 for
 * rounding), in ascending id order, so the search gives exactly the brute force's owner/neighbour/ties.
 * Typical lists: ~3.3 seeds, at most ~8 (35…256 seeds).
 */
type Grid = {
  key: Float32Array;
  n: number;
  M: number;
  /** normalized seeds (exactly as the brute force normalizes them) */
  sx: Float64Array; sy: Float64Array; sz: Float64Array;
  /** CSR: cell → [off[cell], off[cell + 1]) into cand (xyz) / cid */
  off: Int32Array;
  cand: Float64Array;
  cid: Uint16Array;
};

// the last grids (plates and fine cells), reused while the same seeds are computed in row slices (main-thread fallback)
const grids: Grid[] = [];

const capP = new Float64Array(3);
function faceDir(f: number, u: number, v: number) {
  const a = f >> 1, sgn = f & 1 ? -1 : 1;
  let x: number, y: number, z: number;
  if (a === 0) { x = sgn; y = u; z = v; } else if (a === 1) { x = u; y = sgn; z = v; } else { x = u; y = v; z = sgn; }
  const l = Math.sqrt(x * x + y * y + z * z);
  capP[0] = x / l; capP[1] = y / l; capP[2] = z / l;
}

/** Appends to `out`, in `src` order, the seeds of `src` within a₂ + 2r of cell (f, iu, iv) of an m×m face grid. */
function nearSeeds(f: number, iu: number, iv: number, m: number, src: Uint16Array, sx: Float64Array, sy: Float64Array, sz: Float64Array, out: number[]) {
  const u0 = -1 + (2 * iu) / m, u1 = -1 + (2 * (iu + 1)) / m, v0 = -1 + (2 * iv) / m, v1 = -1 + (2 * (iv + 1)) / m;
  faceDir(f, (u0 + u1) / 2, (v0 + v1) / 2);
  const cx = capP[0], cy = capP[1], cz = capP[2];
  let cosR = 1;
  for (let k = 0; k < 4; k++) {
    faceDir(f, k & 1 ? u1 : u0, k & 2 ? v1 : v0);
    cosR = Math.min(cosR, cx * capP[0] + cy * capP[1] + cz * capP[2]);
  }
  const r = Math.acos(Math.max(-1, Math.min(1, cosR)));
  let e1 = -3, e2 = -3;
  for (let k = 0; k < src.length; k++) {
    const s = src[k], d = sx[s] * cx + sy[s] * cy + sz[s] * cz;
    if (d > e1) { e2 = e1; e1 = d; } else if (d > e2) e2 = d;
  }
  // one or two seeds: e2 = −3 → reach ≥ π → everything
  const reach = Math.acos(Math.max(-1, Math.min(1, e2))) + 2 * r + 1e-6;
  const thr = reach >= Math.PI ? -3 : Math.cos(reach);
  for (let k = 0; k < src.length; k++) {
    const s = src[k];
    if (sx[s] * cx + sy[s] * cy + sz[s] * cz >= thr) out.push(s);
  }
}

function seedGrid(seeds: Float32Array): Grid {
  for (const g0 of grids) {
    if (g0.key.length !== seeds.length) continue;
    let same = true;
    for (let i = 0; i < seeds.length; i++) if (g0.key[i] !== seeds[i]) { same = false; break; }
    if (same) return g0;
  }
  const n = (seeds.length / 3) | 0;
  const sx = new Float64Array(n), sy = new Float64Array(n), sz = new Float64Array(n);
  for (let s = 0; s < n; s++) {
    const l = Math.hypot(seeds[s * 3], seeds[s * 3 + 1], seeds[s * 3 + 2]) || 1;
    sx[s] = seeds[s * 3] / l; sy[s] = seeds[s * 3 + 1] / l; sz[s] = seeds[s * 3 + 2] / l;
  }
  // two levels: coarse cells test every seed, each coarse cell's 4×4 fine cells test only its list (a fine
  // cell lies inside its coarse cell, so the coarse list holds every seed any of its points can need,
  // including the fine centre's own top two). ~1.5 fine cells per seed per face edge → lists of ~5–8 seeds.
  const Q = 4;
  const Mc = Math.max(1, Math.min(10, Math.round(Math.sqrt(n) * 0.55))), M = Mc * Q;
  const all = new Uint16Array(n);
  for (let s = 0; s < n; s++) all[s] = s;
  const coarse: Uint16Array[] = [];
  const tmp: number[] = [];
  for (let f = 0; f < 6; f++) for (let iv = 0; iv < Mc; iv++) for (let iu = 0; iu < Mc; iu++) {
    tmp.length = 0;
    nearSeeds(f, iu, iv, Mc, all, sx, sy, sz, tmp);
    coarse.push(Uint16Array.from(tmp));
  }
  const cells = 6 * M * M;
  const off = new Int32Array(cells + 1);
  const ids: number[] = [];
  for (let f = 0, cell = 0; f < 6; f++) for (let iv = 0; iv < M; iv++) for (let iu = 0; iu < M; iu++, cell++) {
    off[cell] = ids.length;
    nearSeeds(f, iu, iv, M, coarse[(f * Mc + ((iv / Q) | 0)) * Mc + ((iu / Q) | 0)], sx, sy, sz, ids);
  }
  off[cells] = ids.length;
  const cand = new Float64Array(ids.length * 3), cid = new Uint16Array(ids.length);
  for (let k = 0; k < ids.length; k++) {
    const s = ids[k];
    cid[k] = s; cand[k * 3] = sx[s]; cand[k * 3 + 1] = sy[s]; cand[k * 3 + 2] = sz[s];
  }
  const g: Grid = { key: seeds.slice(), n, M, sx, sy, sz, off, cand, cid };
  grids.unshift(g);
  if (grids.length > 2) grids.length = 2;
  return g;
}

/**
 * Computes rows [y0, y1) of the field into `out` (allocated by the first call when omitted).
 * Splitting into row ranges lets a main-thread fallback spread the work over idle slices.
 * Exactly the brute-force nearest-two search over all seeds, but each texel only tests its grid cell's
 * candidates (seedGrid; ~6 of 250) and the warp noise reuses its lattice hashes along the row.
 */
export function computeField(seeds: Float32Array, y0 = 0, y1 = FIELD_H, out?: FieldResult, W = FIELD_W, H = FIELD_H, warp = 0.085, base = 0): FieldResult {
  const t0 = typeof performance !== "undefined" ? performance.now() : 0;
  const g = seedGrid(seeds);
  const n = g.n, M = g.M, halfM = M * 0.5, sx = g.sx, sy = g.sy, sz = g.sz, off = g.off, cand = g.cand, cid = g.cid;
  const res = out ?? { ids: new Uint8Array(W * H * 2), dist: new Uint8Array(W * H), area: new Float32Array(n), w: W, h: H, ms: 0 };
  const ids = res.ids, dist = res.dist, area = res.area;
  const cosPhi = new Float64Array(W), sinPhi = new Float64Array(W);
  for (let x = 0; x < W; x++) { const phi = ((x + 0.5) / W) * Math.PI * 2; cosPhi[x] = Math.cos(phi); sinPhi[x] = Math.sin(phi); }
  const texelAngle = Math.PI / FIELD_H; // distances in the 1024×512 field's texels at any size
  const pixArea = (Math.PI * 2 / W) * (Math.PI / H);
  const warp2 = warp * 0.28;
  // hash caches start on a NaN key (never matches)
  const c1 = new Float64Array(11).fill(NaN), c2 = new Float64Array(11).fill(NaN), c3 = new Float64Array(11).fill(NaN);
  const c4 = new Float64Array(11).fill(NaN), c5 = new Float64Array(11).fill(NaN), c6 = new Float64Array(11).fill(NaN);
  for (let y = y0; y < y1; y++) {
    const theta = (1 - (y + 0.5) / H) * Math.PI, st = Math.sin(theta), ct = Math.cos(theta);
    const rowArea = pixArea * st;
    for (let x = 0; x < W; x++) {
      const dx = -cosPhi[x] * st, dy = ct, dz = sinPhi[x] * st;
      // domain warp → jagged, crack-like borders
      let wx = dx + warp * vnoise(dx * 7 + 3.1, dy * 7, dz * 7, c1) + warp2 * vnoise(dx * 23, dy * 23 + 1.7, dz * 23, c2);
      let wy = dy + warp * vnoise(dx * 7, dy * 7 + 5.7, dz * 7, c3) + warp2 * vnoise(dx * 23 + 4.4, dy * 23, dz * 23, c4);
      let wz = dz + warp * vnoise(dx * 7, dy * 7, dz * 7 + 9.3, c5) + warp2 * vnoise(dx * 23, dy * 23, dz * 23 + 8.1, c6);
      const wl = Math.sqrt(wx * wx + wy * wy + wz * wz) || 1; wx /= wl; wy /= wl; wz /= wl;
      // cube-map cell of the warped direction
      const ax = Math.abs(wx), ay = Math.abs(wy), az = Math.abs(wz);
      let f: number, u: number, v: number;
      if (ax >= ay && ax >= az) { f = wx > 0 ? 0 : 1; u = wy / ax; v = wz / ax; }
      else if (ay >= az) { f = wy > 0 ? 2 : 3; u = wx / ay; v = wz / ay; }
      else { f = wz > 0 ? 4 : 5; u = wx / az; v = wy / az; }
      let iu = ((u + 1) * halfM) | 0, iv = ((v + 1) * halfM) | 0;
      if (iu >= M) iu = M - 1;
      if (iv >= M) iv = M - 1;
      const cell = (f * M + iv) * M + iu;
      let b1 = 0, d1 = -3, b2 = 0, d2 = -3;
      for (let k = off[cell], k1 = off[cell + 1]; k < k1; k++) {
        const dp = cand[k * 3] * wx + cand[k * 3 + 1] * wy + cand[k * 3 + 2] * wz;
        if (dp > d1) { d2 = d1; b2 = b1; d1 = dp; b1 = cid[k]; } else if (dp > d2) { d2 = dp; b2 = cid[k]; }
      }
      // distance from the (warped) point to the bisector plane of the two nearest seeds
      const ex = sx[b1] - sx[b2], ey = sy[b1] - sy[b2], ez = sz[b1] - sz[b2];
      const el = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1;
      const ang = Math.max(0, (wx * ex + wy * ey + wz * ez) / el); // ≈ radians for small values
      const i = (y - base) * W + x;
      ids[i * 2] = b1; ids[i * 2 + 1] = b2;
      dist[i] = Math.min(255, Math.round((ang / texelAngle) * DIST_SCALE));
      area[b1] += rowArea;
    }
  }
  if (typeof performance !== "undefined") res.ms += performance.now() - t0;
  return res;
}

/**
 * Plate seeds for one ball: an even spread over the whole sphere (no weak-spot cluster), each plate with
 * its own size — mostly small chips, some slabs about twice their area, mixed at random like real crazed
 * wax. Soft-disc relaxation: every seed wants a neighbour spacing ∝ its size, and the discs are slightly
 * too big for the sphere so they press evenly against each other. Deterministic for `rng`.
 * `count` is a plate count or a [min, max] range; clamped to 1..256 so plate ids stay 8-bit.
 */
export function makeSeeds(rng: () => number, count: number | [number, number] = [30, 39]): Float32Array {
  const want = typeof count === "number" ? Math.round(count) : count[0] + Math.floor(rng() * (count[1] - count[0] + 1));
  const n = Math.max(1, Math.min(256, want || 1));
  const px = new Float64Array(n), py = new Float64Array(n), pz = new Float64Array(n), size = new Float64Array(n);
  let sum2 = 0;
  for (let i = 0; i < n; i++) {
    // stratified in z (one band per seed, bands shuffled by the random azimuth) so the start is already even-ish
    const z = 1 - (2 * (i + rng())) / n, a = rng() * Math.PI * 2, r = Math.sqrt(Math.max(0, 1 - z * z));
    px[i] = r * Math.cos(a); py[i] = r * Math.sin(a); pz[i] = z;
    // linear size 0.78…1.48 skewed small; Voronoi bisectors average neighbours, so areas end up ≈ 1 : 2 (p10 : p90)
    const t = rng();
    size[i] = 0.78 + 0.7 * t * t * Math.sqrt(t);
    sum2 += size[i] * size[i];
  }
  if (n < 3) return seedsOut(px, py, pz, n, rng);
  // hexagonal packing: plate area a ↔ neighbour spacing ≈ 1.075·√a, with area_i = 4π · size_i² / Σ size²
  const k = 1.075 * Math.sqrt((4 * Math.PI) / sum2) * 1.08;
  const dx = new Float64Array(n), dy = new Float64Array(n), dz = new Float64Array(n);
  const iters = 28;
  for (let it = 0; it < iters; it++) {
    dx.fill(0); dy.fill(0); dz.fill(0);
    const step = 0.5 * (1 - it / (iters + 4));
    for (let i = 0; i < n; i++) {
      const xi = px[i], yi = py[i], zi = pz[i], si = size[i];
      for (let j = i + 1; j < n; j++) {
        const ex = xi - px[j], ey = yi - py[j], ez = zi - pz[j];
        const d2 = ex * ex + ey * ey + ez * ez;
        const gap = k * (si + size[j]) * 0.5;
        if (d2 >= gap * gap) continue;
        const d = Math.sqrt(d2) || 1e-9;
        const m = ((gap - d) / d) * 0.5 * step;
        dx[i] += ex * m; dy[i] += ey * m; dz[i] += ez * m;
        dx[j] -= ex * m; dy[j] -= ey * m; dz[j] -= ez * m;
      }
    }
    for (let i = 0; i < n; i++) {
      const x = px[i] + dx[i], y = py[i] + dy[i], z = pz[i] + dz[i];
      const l = Math.sqrt(x * x + y * y + z * z) || 1;
      px[i] = x / l; py[i] = y / l; pz[i] = z / l;
    }
  }
  return seedsOut(px, py, pz, n, rng);
}

/** Packs the seeds in a shuffled order so plate ids carry no position pattern (the start was banded). */
function seedsOut(px: Float64Array, py: Float64Array, pz: Float64Array, n: number, rng: () => number) {
  const order = new Uint16Array(n);
  for (let i = 0; i < n; i++) order[i] = i;
  for (let i = n - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)), t = order[i]; order[i] = order[j]; order[j] = t; }
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { const o = order[i]; out[i * 3] = px[o]; out[i * 3 + 1] = py[o]; out[i * 3 + 2] = pz[o]; }
  return out;
}

/**
 * Seeds of the finer crack layer: `n` cells spread evenly over the sphere (a jittered Fibonacci spiral, O(n),
 * deterministic for `seed`). Built in the worker; nothing else needs them.
 */
export function fineSeeds(seed: number, n: number): Float32Array {
  let a = seed >>> 0;
  const rng = () => { a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const out = new Float32Array(n * 3), golden = Math.PI * (3 - Math.sqrt(5)), jit = 0.55;
  for (let i = 0; i < n; i++) {
    const z = 1 - (2 * (i + 0.5 + (rng() - 0.5) * jit)) / n, r = Math.sqrt(Math.max(0, 1 - z * z));
    const t = i * golden + (rng() - 0.5) * jit * (2 * Math.PI / Math.sqrt(n));
    out[i * 3] = r * Math.cos(t); out[i * 3 + 1] = z; out[i * 3 + 2] = r * Math.sin(t);
  }
  return out;
}

/**
 * Rows [y0, y1) of the finer crack layer into `out` (RG pairs): per texel, the distance to the nearest border
 * between the fine cells (texels × DIST_SCALE) and its cell's random byte, with its own jagged warp. Same grid
 * search as computeField.
 */
export function computeFine(seeds: Float32Array, y0 = 0, y1 = FIELD_H, out?: Uint8Array, W = FIELD_W, H = FIELD_H, warp = 0.034, base = 0): Uint8Array {
  const g = seedGrid(seeds);
  const M = g.M, halfM = M * 0.5, sx = g.sx, sy = g.sy, sz = g.sz, off = g.off, cand = g.cand, cid = g.cid;
  const res = out ?? new Uint8Array(W * H * 2);
  const texelAngle = Math.PI / FIELD_H, warp2 = warp * 0.5;
  const c1 = new Float64Array(11).fill(NaN), c2 = new Float64Array(11).fill(NaN), c3 = new Float64Array(11).fill(NaN);
  const c4 = new Float64Array(11).fill(NaN), c5 = new Float64Array(11).fill(NaN), c6 = new Float64Array(11).fill(NaN);
  const cosPhi = new Float64Array(W), sinPhi = new Float64Array(W);
  for (let x = 0; x < W; x++) { const phi = ((x + 0.5) / W) * Math.PI * 2; cosPhi[x] = Math.cos(phi); sinPhi[x] = Math.sin(phi); }
  for (let y = y0; y < y1; y++) {
    const theta = (1 - (y + 0.5) / H) * Math.PI, st = Math.sin(theta), ct = Math.cos(theta);
    for (let x = 0; x < W; x++) {
      const dx = -cosPhi[x] * st, dy = ct, dz = sinPhi[x] * st;
      let wx = dx + warp * vnoise(dx * 29 + 3.1, dy * 29, dz * 29, c1) + warp2 * vnoise(dx * 83, dy * 83 + 1.7, dz * 83, c2);
      let wy = dy + warp * vnoise(dx * 29, dy * 29 + 5.7, dz * 29, c3) + warp2 * vnoise(dx * 83 + 4.4, dy * 83, dz * 83, c4);
      let wz = dz + warp * vnoise(dx * 29, dy * 29, dz * 29 + 9.3, c5) + warp2 * vnoise(dx * 83, dy * 83, dz * 83 + 8.1, c6);
      const wl = Math.sqrt(wx * wx + wy * wy + wz * wz) || 1; wx /= wl; wy /= wl; wz /= wl;
      const ax = Math.abs(wx), ay = Math.abs(wy), az = Math.abs(wz);
      let f: number, u: number, v: number;
      if (ax >= ay && ax >= az) { f = wx > 0 ? 0 : 1; u = wy / ax; v = wz / ax; }
      else if (ay >= az) { f = wy > 0 ? 2 : 3; u = wx / ay; v = wz / ay; }
      else { f = wz > 0 ? 4 : 5; u = wx / az; v = wy / az; }
      let iu = ((u + 1) * halfM) | 0, iv = ((v + 1) * halfM) | 0;
      if (iu >= M) iu = M - 1;
      if (iv >= M) iv = M - 1;
      const cell = (f * M + iv) * M + iu;
      let b1 = 0, d1 = -3, b2 = 0, d2 = -3;
      for (let k = off[cell], k1 = off[cell + 1]; k < k1; k++) {
        const dp = cand[k * 3] * wx + cand[k * 3 + 1] * wy + cand[k * 3 + 2] * wz;
        if (dp > d1) { d2 = d1; b2 = b1; d1 = dp; b1 = cid[k]; } else if (dp > d2) { d2 = dp; b2 = cid[k]; }
      }
      const ex = sx[b1] - sx[b2], ey = sy[b1] - sy[b2], ez = sz[b1] - sz[b2];
      const el = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1;
      const ang = Math.max(0, (wx * ex + wy * ey + wz * ez) / el);
      const i = ((y - base) * W + x) * 2;
      res[i] = Math.min(255, Math.round((ang / texelAngle) * DIST_SCALE));
      res[i + 1] = (Math.imul(b1 + 1, 0x9e3779b1) >>> 24) & 255;
    }
  }
  return res;
}

/** Rows [y0, y1) of the plate field (rows of the res-scaled map), as the worker and the idle-slice fallback run it. */
export function fieldRows(seeds: Float32Array, res: number, y0: number, y1: number, out?: FieldResult) {
  return computeField(seeds, y0, y1, out, FIELD_W * res, FIELD_H * res);
}
/** Rows [y0, y1) of the fine layer at res. */
export function fineRows(seeds: Float32Array, res: number, y0: number, y1: number, out?: Uint8Array) {
  return computeFine(seeds, y0, y1, out, FIELD_W * res, FIELD_H * res);
}
/**
 * Rows [y0, y1) of a field at `res` and its fine layer, in arrays of just that band (row y0 first): one worker's share
 * when several split a big field. `area` holds this band's part of each plate's area (the shares add up).
 */
export function fieldBand(seeds: Float32Array, res: number, fineSeed: number, fineCount: number, y0: number, y1: number): FieldResult {
  const W = FIELD_W * res, H = FIELD_H * res, n = (y1 - y0) * W;
  const r = computeField(seeds, y0, y1, { ids: new Uint8Array(n * 2), dist: new Uint8Array(n), area: new Float32Array(seeds.length / 3), w: W, h: H, ms: 0 }, W, H, undefined, y0);
  if (fineCount) r.fine = computeFine(fineSeeds(fineSeed, fineCount), y0, y1, new Uint8Array(n * 2), W, H, undefined, y0);
  return r;
}

/** A whole field at `res` (1 = 1024×512, 2 = 2048×1024, 4 = 4096×2048), with its fine layer when `fineCount` > 0. */
export function computeFieldAt(seeds: Float32Array, res: number, fineSeed: number, fineCount: number): FieldResult {
  const H = FIELD_H * res, r = fieldRows(seeds, res, 0, H);
  if (fineCount) r.fine = fineRows(fineSeeds(fineSeed, fineCount), res, 0, H);
  return r;
}
